import React, { useEffect, useState, useCallback, useRef } from "react";
import ThemeProvider from "../theme/TemaMuiProvider";
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  Paper,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SearchIcon from "@mui/icons-material/Search";
import { jwtDecode } from "jwt-decode";
import { copiarTexto } from "../utils/clipboard";
import muiTheme from "../theme/muiTheme";
import { FaEdit, FaSave, FaTimes } from "react-icons/fa";

const formVazio = { descricao: "" };

export default function IndicesReajuste() {
  const [indices, setIndices] = useState([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);

  const [inputFiltro, setInputFiltro] = useState("");
  const [filtro, setFiltro] = useState("");
  const [pagina, setPagina] = useState(0);
  const [porPagina, setPorPagina] = useState(5);
  const [gridMinimizado, setGridMinimizado] = useState(() => {
    const saved = localStorage.getItem("indicesReajusteGridMinimizado");
    return saved ? saved === "1" : false;
  });

  useEffect(() => {
    localStorage.setItem("indicesReajusteGridMinimizado", gridMinimizado ? "1" : "0");
  }, [gridMinimizado]);

  const descricaoInputRef = useRef(null);

  // form inferior: null = novo, número = id selecionado
  const [editandoId, setEditandoId] = useState(null);
  const [somenteLeitura, setSomenteLeitura] = useState(false);
  const [form, setForm] = useState({ ...formVazio });

  const [confirmDelete, setConfirmDelete] = useState({ aberto: false, id: null });
  const [errosForm, setErrosForm] = useState({});

  const [snack, setSnack] = useState({ aberto: false, msg: "", severity: "success" });
  const mostrarSnack = (msg, severity = "success") => setSnack({ aberto: true, msg, severity });

  // permissões — refletem exatamente o que o backend exige em cada rota
  const token = localStorage.getItem("token");
  let tipoUsuario = "";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      tipoUsuario = decoded.tipo?.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    } catch (_) {}
  }
  const podeVisualizar = ["visitante", "usuario padrao", "gestor", "administrador"].includes(tipoUsuario);
  const podeCadastrar = ["usuario padrao", "gestor", "administrador"].includes(tipoUsuario); // POST
  const podeEditar = ["gestor", "administrador"].includes(tipoUsuario); // PUT
  const podeExcluir = ["administrador"].includes(tipoUsuario); // DELETE

  const carregarIndices = useCallback(() => {
    const tk = localStorage.getItem("token");
    const params = new URLSearchParams({ filtro, pagina, porPagina });
    setCarregando(true);
    fetch(`/api/indices-reajuste?${params}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => r.json())
      .then(({ data, total: t }) => {
        setIndices(Array.isArray(data) ? data : []);
        setTotal(t ?? 0);
      })
      .catch((err) => console.error("Erro ao buscar índices de reajuste:", err))
      .finally(() => setCarregando(false));
  }, [filtro, pagina, porPagina]);

  useEffect(() => {
    if (!podeVisualizar) return;
    carregarIndices();
  }, [podeVisualizar, carregarIndices]);

  // ── Pesquisa automática: aplica o filtro digitado após o usuário parar de digitar
  useEffect(() => {
    const t = setTimeout(() => {
      setFiltro(inputFiltro);
      setPagina(0);
    }, 400);
    return () => clearTimeout(t);
  }, [inputFiltro]);

  const handlePesquisar = () => {
    setFiltro(inputFiltro);
    setPagina(0);
  };
  const handleLimparFiltro = () => {
    setInputFiltro("");
    setFiltro("");
    setPagina(0);
  };

  const handleCopiar = async () => {
    const texto = indices.map((indice) => `${indice.id} - ${indice.descricao}`).join(", ");

    if (!texto.trim()) {
      alert("Nada para copiar (lista vazia).");
      return;
    }

    const ok = await copiarTexto(texto);

    alert(ok ? "Copiado!" : "Não foi possível copiar.");
  };

  // =======================
  // CSV
  // =======================
  const handleCSV = () => {
    const BOM = "\uFEFF";

    const header = ["Código", "Descrição"];

    const csvEscape = (value) => {
      const texto = String(value ?? "");

      if (/[;"\n\r]/.test(texto)) {
        return `"${texto.replace(/"/g, '""')}"`;
      }

      return texto;
    };

    const linhas = indices.map((indice) => [indice.id ?? "", indice.descricao || ""].map(csvEscape));

    const csv = [header.map(csvEscape), ...linhas].map((linha) => linha.join(";")).join("\n");

    const blob = new Blob([BOM + csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = url;

    link.setAttribute("download", "indices-reajuste.csv");

    document.body.appendChild(link);

    link.click();

    link.remove();

    URL.revokeObjectURL(url);
  };

  // ── Abrir em modo visualização ──────────────────────────────────────────
  const handleVisualizar = (indice) => {
    setEditandoId(indice.id);
    setSomenteLeitura(true);
    setForm({ descricao: indice.descricao });
    setGridMinimizado(true);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    setTimeout(() => descricaoInputRef.current?.focus(), 300);
  };

  const handleHabilitarEdicao = () => {
    setSomenteLeitura(false);
    setTimeout(() => descricaoInputRef.current?.focus(), 50);
  };

  const handleCancelarForm = () => {
    setEditandoId(null);
    setSomenteLeitura(false);
    setForm({ ...formVazio });
    setErrosForm({});
    setGridMinimizado(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // ── Salvar (criar ou editar) ─────────────────────────────────────────────
  const handleSalvar = () => {
    if (!form.descricao.trim()) {
      setErrosForm({ descricao: "Descrição é obrigatória." });
      descricaoInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setErrosForm({});

    const tk = localStorage.getItem("token");
    const url = editandoId ? `/api/indices-reajuste/${editandoId}` : "/api/indices-reajuste";
    const method = editandoId ? "PUT" : "POST";

    fetch(url, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${tk}` },
      body: JSON.stringify(form),
    })
      .then(async (res) => {
        if (!res.ok) {
          const d = await res.json();
          mostrarSnack(d.erro || "Erro.", "error");
          return;
        }
        mostrarSnack(editandoId ? "Índice de Reajuste atualizado!" : "Índice de Reajuste cadastrado com sucesso!");
        handleCancelarForm();
        carregarIndices();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  // ── Excluir ───────────────────────────────────────────────────────────────
  const handleConfirmarDelete = (id) => {
    document.activeElement?.blur();
    setConfirmDelete({ aberto: true, id });
  };

  const handleExcluir = () => {
    const { id } = confirmDelete;
    setConfirmDelete({ aberto: false, id: null });
    const tk = localStorage.getItem("token");
    fetch(`/api/indices-reajuste/${id}`, { method: "DELETE", headers: { Authorization: `Bearer ${tk}` } })
      .then(async (res) => {
        if (!res.ok && res.status !== 204) {
          const d = await res.json().catch(() => null);
          mostrarSnack(d?.erro || "Erro ao excluir.", "error");
          return;
        }
        if (editandoId === id) handleCancelarForm();
        mostrarSnack("Índice de Reajuste excluído.", "warning");
        carregarIndices();
      })
      .catch(() => mostrarSnack("Erro de conexão.", "error"));
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Box sx={{ color: "var(--ig-t-ffffff)" }}>
        {/* Título */}
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Índices de Reajuste
        </Typography>

        {/* ── Filtro + Copiar/CSV + Minimizar ─────────────────────────────── */}
        <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
          <TextField
            size="small"
            placeholder="Filtrar Índices de Reajuste"
            value={inputFiltro}
            onChange={(e) => setInputFiltro(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePesquisar();
            }}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "var(--ig-t-9ca3af)", fontSize: 20 }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-root": { backgroundColor: "transparent", color: "var(--ig-t-ffffff)" },
              "& input::placeholder": { color: "var(--ig-t-ffffff)", opacity: 1, fontWeight: 500, fontSize: "1rem" },
            }}
          />
          <Button
            size="small"
            variant="outlined"
            type="button"
            onClick={handleLimparFiltro}
            sx={{
              flexShrink: 0,
              width: "auto",
              height: 40,
              color: "#2196f3",
              borderColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              whiteSpace: "nowrap",
              "&:hover": { borderColor: "#42a5f5", backgroundColor: "rgba(33, 150, 243, 0.08)" },
            }}
          >
            Limpar Filtro
          </Button>
          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCopiar}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            <i
              className="fas fa-copy"
              style={{
                marginRight: 6,
              }}
            />
            Copiar
          </Button>

          <Button
            size="small"
            variant="contained"
            type="button"
            onClick={handleCSV}
            sx={{
              height: 40,
              backgroundColor: "#2196f3",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "#1976d2",
              },
            }}
          >
            CSV
          </Button>
        </Box>

        {/* ── Minimizar grid ───────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 1 }}>
          {!gridMinimizado ? (
            <button
              type="button"
              onClick={() => setGridMinimizado(true)}
              style={{
                border: "1px solid #d32f2f",
                color: "#ff4c4c",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↘ Minimizar
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setGridMinimizado(false)}
              style={{
                border: "1px solid #1998a8",
                color: "#1998a8",
                background: "transparent",
                padding: "5px 12px",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: 13,
                width: "fit-content",
                display: "inline-block",
              }}
            >
              ↗ Expandir
            </button>
          )}
        </Box>

        {/* ── Grid ─────────────────────────────────────────────────────────── */}
        <Collapse in={!gridMinimizado}>
          <TableContainer component={Paper} sx={{ mb: 3, backgroundColor: "transparent", boxShadow: "none" }}>
            {carregando ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Table size="small" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "0.95rem" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Ações
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: "100px",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Código
                    </TableCell>
                    <TableCell
                      sx={{
                        backgroundColor: "var(--ig-f-1e1e1e)",
                        color: "var(--ig-t-ffffff)",
                        fontWeight: "bold",
                        borderBottom: "1px solid var(--ig-b-444444)",
                        width: "100%",
                      }}
                    >
                      Descrição
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {indices.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ color: "var(--ig-t-888888)", py: 3, borderBottom: "1px solid var(--ig-b-444444)" }}>
                        Nenhum índice de reajuste encontrado.
                      </TableCell>
                    </TableRow>
                  ) : (
                    indices.map((indice) => (
                      <TableRow
                        key={indice.id}
                        sx={{
                          "&:hover": { backgroundColor: "var(--ig-f-2a2a2a)" },
                          backgroundColor: editandoId === indice.id ? "rgba(25,152,168,0.08)" : "transparent",
                        }}
                      >
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", whiteSpace: "nowrap" }}>
                          {podeVisualizar && (
                            <Tooltip title="Visualizar / Editar">
                              <IconButton
                                size="small"
                                onClick={() => handleVisualizar(indice)}
                                sx={{
                                  backgroundColor: "#1998a8",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#1565c0" },
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {podeExcluir && (
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={() => handleConfirmarDelete(indice.id)}
                                sx={{
                                  backgroundColor: "#d32f2f",
                                  color: "#fff",
                                  borderRadius: "4px",
                                  mx: 0.25,
                                  width: "auto",
                                  "&:hover": { backgroundColor: "#c62828" },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", whiteSpace: "nowrap" }}>
                          {indice.id}
                        </TableCell>
                        <TableCell sx={{ borderBottom: "1px solid var(--ig-b-444444)", color: "var(--ig-t-ffffff)", width: "100%" }}>
                          {indice.descricao}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}

            {/* Paginação */}
            {!carregando && (
              <TablePagination
                component="div"
                count={total}
                page={pagina}
                onPageChange={(_, p) => setPagina(p)}
                rowsPerPage={porPagina}
                onRowsPerPageChange={(e) => {
                  setPorPagina(parseInt(e.target.value, 10));
                  setPagina(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage="Índices por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                sx={{
                  color: "var(--ig-t-ffffff)",
                  borderTop: "1px solid var(--ig-b-444444)",
                  "& .MuiTablePagination-selectIcon": { color: "var(--ig-t-ffffff)" },
                  "& .MuiTablePagination-actions button": { color: "var(--ig-t-ffffff)", width: "auto" },
                  "& .MuiTablePagination-actions": { display: "flex", alignItems: "center" },
                }}
              />
            )}
          </TableContainer>
        </Collapse>

        {/* ── Formulário inferior (visualizar / criar / editar) ────────────── */}
        {(podeCadastrar || (podeVisualizar && editandoId)) && (
          <Paper sx={{ p: 3, backgroundColor: "var(--ig-f-1e1e1e)", borderRadius: 2, boxShadow: "0 0 20px rgba(0,0,0,0.4)" }}>
            <Typography variant="h6" sx={{ mb: 2, color: "var(--ig-t-ffffff)", fontWeight: 600 }}>
              {editandoId && somenteLeitura ? "Visualizar Índice de Reajuste" : editandoId ? "Editar Índice de Reajuste" : "Novo Índice de Reajuste"}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Descrição*"
                size="small"
                fullWidth
                inputRef={descricaoInputRef}
                value={form.descricao}
                onChange={(e) => {
                  if (!somenteLeitura) {
                    setForm((p) => ({ ...p, descricao: e.target.value.toUpperCase() }));
                    if (errosForm.descricao) setErrosForm((p) => ({ ...p, descricao: undefined }));
                  }
                }}
                onBlur={() => {
                  if (!somenteLeitura && !form.descricao.trim()) setErrosForm((p) => ({ ...p, descricao: "Descrição é obrigatória." }));
                }}
                error={!!errosForm.descricao}
                helperText={errosForm.descricao}
                slotProps={{ input: { readOnly: somenteLeitura }, htmlInput: { maxLength: 100 } }}
                sx={{
                  "& .MuiInputLabel-root": { color: errosForm.descricao ? "#f44336" : "var(--ig-t-ffffff)", fontWeight: "bold" },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: somenteLeitura ? "var(--ig-f-252525)" : "var(--ig-f-2a2a2a)",
                    color: "var(--ig-t-ffffff)",
                    "& fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.descricao ? "#f44336" : "var(--ig-b-444444)" },
                    "&:hover fieldset": { borderColor: somenteLeitura ? "var(--ig-b-333333)" : errosForm.descricao ? "#f44336" : "var(--ig-b-888888)" },
                  },
                  "& input": { color: "var(--ig-t-ffffff)" },
                  "& .MuiFormHelperText-root": { color: "#f44336" },
                }}
              />

              <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                {/* Modo visualização */}
                {editandoId && somenteLeitura && (
                  <>
                    <Button
                      variant="outlined"
                      onClick={handleCancelarForm}
                      sx={{
                        width: "auto",
                        borderColor: "var(--ig-b-555555)",
                        color: "var(--ig-t-cccccc)",
                        "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                        fontWeight: "bold",
                        px: 3,
                      }}
                    >
                      Fechar
                    </Button>
                    {podeEditar && (
                      <Button
                        variant="contained"
                        startIcon={<FaEdit />}
                        onClick={handleHabilitarEdicao}
                        sx={{ width: "auto", backgroundColor: "#1998a8", "&:hover": { backgroundColor: "#1565c0" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Editar
                      </Button>
                    )}
                    {podeExcluir && (
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        onClick={() => handleConfirmarDelete(editandoId)}
                        sx={{ width: "auto", backgroundColor: "#d32f2f", "&:hover": { backgroundColor: "#c62828" }, fontWeight: "bold", py: 1, px: 3 }}
                      >
                        Excluir
                      </Button>
                    )}
                  </>
                )}
                {/* Modo edição / criação */}
                {(!editandoId || !somenteLeitura) && (
                  <>
                    {editandoId && (
                      <Button
                        variant="outlined"
                        startIcon={<FaTimes />}
                        onClick={handleCancelarForm}
                        sx={{
                          width: "auto",
                          borderColor: "var(--ig-b-555555)",
                          color: "var(--ig-t-cccccc)",
                          "&:hover": { backgroundColor: "var(--ig-f-255-255-255-a005)" },
                          fontWeight: "bold",
                          px: 3,
                        }}
                      >
                        Cancelar
                      </Button>
                    )}
                    {(editandoId ? podeEditar : podeCadastrar) && (
                      <Button
                        variant="contained"
                        startIcon={<FaSave />}
                        onClick={handleSalvar}
                        sx={{ width: "auto", backgroundColor: "#2e7d32", "&:hover": { backgroundColor: "#1b5e20" }, fontWeight: "bold", py: 1, px: 4 }}
                      >
                        {editandoId ? "Atualizar" : "Cadastrar"}
                      </Button>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Paper>
        )}
      </Box>

      {/* ── Dialog: Confirmação de exclusão ──────────────────────────────── */}
      <Dialog open={confirmDelete.aberto} onClose={() => setConfirmDelete({ aberto: false, id: null })}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--ig-t-cccccc)" }}>Deseja excluir este índice de reajuste? Esta ação não pode ser desfeita.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setConfirmDelete({ aberto: false, id: null })}>
            Cancelar
          </Button>
          <Button variant="contained" color="error" onClick={handleExcluir}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────────────── */}
      <Snackbar
        open={snack.aberto}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, aberto: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, aberto: false }))} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
}
